import React, { useMemo, useState } from 'react';
import _t from 'counterpart';
import PageLayout from '../../components/PageLayout';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import { fetchMySalesPerformance, fetchSalesPerson, fetchSalesCommunications } from '../../services/BackendService';
import { formatCurrency } from '../../helpers';
import { CRow, CCol, CCard, CCardBody, CCardHeader, CCardTitle } from '@coreui/react';
import Detail from '../../components/micro/Detail';
import Loading from '../../components/Loading';
import Error from '../../components/Error';
import PaginationTable from '../../components/PaginationTable';
import CommunicationsTable from './CustomerPage/CommunicationsTable';

const customersUnderLimit = 10;

const MyPerformancePage = () => {
  const history = useHistory();
  const [customerUnderPage, setCustomersUnderPage] = useState<number>(1);
  const [communicationsPage, setCommunicationsPage] = useState<number>(1);
  const communicationsLimit = 15;

  const customersUnderOffset =
    Number(customerUnderPage) > 0 ? Number(customerUnderPage) * customersUnderLimit - customersUnderLimit : 0;
  const communicationsOffset =
    Number(communicationsPage) > 0 ? Number(communicationsPage) * communicationsLimit - communicationsLimit : 0;

  const performanceQuery = useQuery(['my-sales-performance'], () => fetchMySalesPerformance());
  const salesPersonId = performanceQuery.data?.salesPerson?.id;

  const salesPersonDetailsQuery = useQuery(
    ['sales-person', salesPersonId, customersUnderLimit, customersUnderOffset],
    () => fetchSalesPerson(salesPersonId!, customersUnderLimit, customersUnderOffset),
    { enabled: !!salesPersonId }
  );

  const communicationsQuery = useQuery(
    ['sales-person-communications', salesPersonId, communicationsOffset],
    () => fetchSalesCommunications(salesPersonId!, communicationsLimit, communicationsOffset),
    { enabled: !!salesPersonId }
  );

  const tableFields = useMemo(
    () => [
      { key: 'name', label: _t('customers.columns.name'), sorter: false },
      { key: 'level', label: _t('customers.columns.level'), sorter: false },
      { key: 'totalDeposit', label: _t('customers.columns.depositTotal'), sorter: false },
      { key: 'totalWithdrawal', label: _t('customers.columns.withdrawalTotal'), sorter: false },
      { key: 'netDeposit', label: _t('customers.columns.netDeposit'), sorter: false },
      { key: 'tradingVolume', label: _t('customers.columns.totalTradingVolume'), sorter: false },
    ],
    []
  );

  const scopedSlots = useMemo(
    () => ({
      name: (row: any) => <td>{row.name}</td>,
      totalDeposit: (row: any) => <td>{formatCurrency(row.totalDeposit)}</td>,
      totalWithdrawal: (row: any) => <td>{formatCurrency(row.totalWithdrawal ?? 0)}</td>,
      netDeposit: (row: any) => <td>{formatCurrency(row.netDeposit ?? 0)}</td>,
      tradingVolume: (row: any) => <td>{row.tradingVolume}</td>,
    }),
    []
  );

  const communicationsFields = useMemo(
    () => [
      { key: 'date', label: _t('global.date'), sorter: false },
      { key: 'to', label: _t('sales-people.recipient'), sorter: false },
      { key: 'type', label: _t('global.type'), sorter: false },
      { key: 'detail', label: _t('global.details'), sorter: false },
    ],
    []
  );

  const onCustomerRowClicked = (user: { id: string }) => {
    history.push(`/customers/${user.id}`);
  };

  const openCustomerInNewTab = (user: { id: string }) => {
    window.open(`/customers/${user.id}`, '_blank');
  };

  if (performanceQuery.isLoading || performanceQuery.isIdle) {
    return <Loading />;
  }

  if (performanceQuery.isError) {
    return <Error onRetry={() => performanceQuery.refetch()} />;
  }

  const { salesPerson } = performanceQuery.data;
  const totalWithdrawal = Number(salesPerson.totalWithdrawal ?? 0);
  const netDeposit = Number(salesPerson.netDeposit ?? 0);

  const { customers, pages: customerUnderPages } = salesPersonDetailsQuery.data ?? {
    customers: [],
    pages: 0,
  };
  const communicationsData = communicationsQuery.data;
  const communicationsPages =
    communicationsData?.count === undefined ? 0 : Math.ceil(Number(communicationsData.count) / communicationsLimit);

  return (
    <PageLayout title={_t('sales-people.my-performance')}>
      <CRow>
        <CCol>
          <CCard>
            <CCardHeader>
              <CCardTitle>{_t('global.info')}</CCardTitle>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol md={6}>
                  <dl>
                    <Detail title={_t('customers.columns.name')}>{salesPerson.name}</Detail>
                    <Detail title={_t('customers.columns.users-under')}>{salesPerson.usersUnder}</Detail>
                    <Detail title={_t('customers.columns.depositTotal')}>
                      {formatCurrency(salesPerson.totalDeposit)}
                    </Detail>
                    <Detail title={_t('customers.columns.withdrawalTotal')}>
                      {formatCurrency(totalWithdrawal)}
                    </Detail>
                  </dl>
                </CCol>
                <CCol md={6}>
                  <dl>
                    <Detail title={_t('customers.columns.netDeposit')}>{formatCurrency(netDeposit)}</Detail>
                    <Detail title={_t('affiliates.affiliate-balance')}>
                      {formatCurrency(salesPerson.balance ?? 0)}
                    </Detail>
                    <Detail title={_t('customers.columns.totalTradingVolume')}>
                      {salesPerson.totalTradingVolume}
                    </Detail>
                  </dl>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CRow>
        <CCol>
          <CCard className="card card--listing">
            <CCardBody className="card__holder">
              <CCardHeader className="card__header">
                <div className="card__info">
                  <h4 className="card__title">{_t('customers.title')}</h4>
                </div>
              </CCardHeader>
              <PaginationTable
                data={salesPersonId ? customers : []}
                tableFields={tableFields}
                scopedSlots={scopedSlots}
                clickableRows
                onRowClicked={onCustomerRowClicked}
                onRowCommandClicked={openCustomerInNewTab}
                loading={salesPersonDetailsQuery.isLoading}
                activePage={customerUnderPage}
                onPageChanged={setCustomersUnderPage}
                pages={customerUnderPages}
                pagination
              />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CRow>
        <CCol>
          <CCard>
            <CommunicationsTable
              data={communicationsQuery.data?.communications || []}
              fields={communicationsFields}
              isLoading={communicationsQuery.isLoading}
              pages={communicationsPages}
              page={communicationsPage}
              onPageChanged={setCommunicationsPage}
            />
          </CCard>
        </CCol>
      </CRow>
    </PageLayout>
  );
};

export default MyPerformancePage;
